/* Auswahl der WADs einer Karte aus der Asset-Liste des Backends (ohne DOM, node-testbar). */
import Backend from './backend.ts';

export interface WadAsset { path: string; sha256?: string }

export interface MapWadPlan {
  /** Namen aus dem wad-Keyvalue (klein geschrieben, ohne Pfad) */
  wanted: string[];
  /** Asset-Pfade, die noch geladen werden müssen */
  load: string[];
  /** schon im Editor geladen */
  have: string[];
  /** auf dem Server nicht vorhanden */
  missing: string[];
  /** geladen, aber mit anderem Inhalt als auf dem Server (gleicher Name, anderer Hash) */
  differ: string[];
}

const base = (p: string): string => p.replace(/^.*\//, '').toLowerCase();

/** loaded: Namen der geladenen WADs im Format „datei.wad (Anzahl)“ oder nur „datei.wad“ */
export function planMapWads(wadKv: unknown, assets: WadAsset[], loaded: string[], hashes: Record<string, string> = {}): MapWadPlan {
  const wanted = Backend.wadNames(wadKv);
  const have = new Set(loaded.map((n) => n.replace(/\s*\(\d+\)\s*$/, '').toLowerCase()));
  const plan: MapWadPlan = { wanted, load: [], have: [], missing: [], differ: [] };
  for (const name of wanted) {
    if (have.has(name)) {
      plan.have.push(name);
      const it = assets.find((a) => base(a.path) === name), mine = hashes[name];
      if (it && it.sha256 && mine && mine !== it.sha256) plan.differ.push(name);
      continue;
    }
    const it = assets.find((a) => base(a.path) === name);
    if (it) plan.load.push(it.path); else plan.missing.push(name);
  }
  return plan;
}

/** SHA-256 als Hex; leer, wenn der Browser keine Web-Crypto bietet (unsicherer Kontext), dann wird nicht verglichen */
export async function sha256Hex(buf: ArrayBuffer): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return '';
  return Array.from(new Uint8Array(await subtle.digest('SHA-256', buf)), (b) => b.toString(16).padStart(2, '0')).join('');
}

export type WadSync =
  | { kind: 'skip' }                                                  // keine WAD
  | { kind: 'upload'; path: string }                                  // Name fehlt auf dem Server
  | { kind: 'same'; path: string }                                    // gleicher Name und gleicher Inhalt (oder Inhalt unbekannt)
  | { kind: 'differs'; path: string; owner?: string };                // gleicher Name, anderer Inhalt

/** Was mit einer lokal geladenen WAD auf dem Server geschehen soll; hash = SHA-256 der Datei (leer = unbekannt) */
export function planWadSync(fileName: string, hash: string, assets: (WadAsset & { owner?: string })[]): WadSync {
  const name = base(fileName.replace(/\\/g, '/'));
  if (!/\.wad$/.test(name)) return { kind: 'skip' };
  const it = assets.find((a) => base(a.path) === name);
  if (!it) return { kind: 'upload', path: Backend.targetPath(name) };
  if (hash && it.sha256 && hash !== it.sha256) return { kind: 'differs', path: it.path, owner: it.owner };
  return { kind: 'same', path: it.path };
}

/** Zielpfad für eine lokal geladene WAD, falls sie auf dem Server (nach Dateiname, ohne Ordner und Groß-/Kleinschreibung) noch fehlt; sonst null */
export function wadUploadPath(fileName: string, assets: { path: string }[]): string | null {
  const p = planWadSync(fileName, '', assets);
  return p.kind === 'upload' ? p.path : null;
}
